(() => {
  const section = document.querySelector('.story-apps--photo-reviews');
  if (!section) return;
  const batchSize = 4;
  let shown = batchSize;
  let loading = false;
  let pending = false;
  let requestedCards = 0;
  let requests = 0;
  let requestTimer;
  let grid;
  let moreButton;

  const nativeMore = () => section.querySelector('.jdgm-reviews-grid-widget .jm-show-more-button');

  const reviewKey = card => {
    const label = card.getAttribute('aria-label') || '';
    const body = card.querySelector('.jdgm-review-card__details-description')?.textContent || '';
    return `${label.trim()}|${body.trim()}`;
  };

  const reviewImage = card => card.querySelector('.jdgm-review-card-media img');

  const imageCopy = (source, number) => {
    const image = document.createElement('img');
    image.src = source.currentSrc || source.src;
    image.alt = `Customer review photo ${number}`;
    image.loading = 'lazy';
    image.decoding = 'async';
    return image;
  };

  const makeComposition = images => {
    const composition = document.createElement('div');
    composition.className = images.length === 2 ? 'cps-grid-pair' : 'cps-grid-collage';

    images.slice(0, 4).forEach((source, index) => composition.append(imageCopy(source, index + 1)));

    if (images.length === 2) {
      composition.style.setProperty('--cps-grid-reveal', '50%');
      const divider = document.createElement('span');
      divider.className = 'cps-grid-divider';
      divider.setAttribute('aria-hidden', 'true');
      composition.append(divider);

      const slider = document.createElement('input');
      slider.className = 'cps-grid-slider';
      slider.type = 'range';
      slider.min = '5';
      slider.max = '95';
      slider.value = '50';
      slider.setAttribute('aria-label', 'Compare the two customer repair photos');
      slider.addEventListener('input', () => composition.style.setProperty('--cps-grid-reveal', `${slider.value}%`));
      for (const eventName of ['click', 'pointerdown', 'keydown']) {
        slider.addEventListener(eventName, event => event.stopPropagation());
      }
      composition.append(slider);
    } else {
      composition.dataset.count = String(Math.min(images.length, 4));
      if (images.length > 4) {
        const more = document.createElement('span');
        more.className = 'cps-grid-photo-count';
        more.textContent = `+${images.length - 4} more`;
        composition.append(more);
      }
    }
    return composition;
  };

  const enhance = grid => {
    const cards = [...grid.children].filter(child => child.classList.contains('jdgm-review-card'));
    for (const card of cards) {
      card.classList.remove('cps-grid-hidden-media', 'cps-grid-hidden-batch', 'cps-grid-wide-card', 'cps-grid-fill-one', 'cps-grid-fill-two', 'cps-grid-fill-three', 'cps-grid-tablet-last', 'cps-grid-interactive');
      card.removeAttribute('aria-hidden');
      card.querySelector('.cps-grid-pair, .cps-grid-collage')?.remove();
      reviewImage(card)?.classList.remove('cps-grid-original-image');
      if (card.dataset.cpsOriginalTabindex !== undefined) card.setAttribute('tabindex', card.dataset.cpsOriginalTabindex);
    }

    for (let index = 0; index < cards.length;) {
      const first = cards[index];
      const key = reviewKey(first);
      const group = [first];
      let next = index + 1;
      while (next < cards.length && key && reviewKey(cards[next]) === key && reviewImage(cards[next])) {
        group.push(cards[next]);
        next += 1;
      }
      const images = group.map(reviewImage);
      const frame = first.querySelector('.jdgm-review-card-media');
      if (group.length > 1 && images.every(Boolean) && frame) {
        frame.append(makeComposition(images));
        if (images.length === 2) first.classList.add('cps-grid-interactive');
        images[0].classList.add('cps-grid-original-image');
        for (const extra of group.slice(1)) {
          extra.classList.add('cps-grid-hidden-media');
          extra.setAttribute('aria-hidden', 'true');
          if (extra.dataset.cpsOriginalTabindex === undefined) extra.dataset.cpsOriginalTabindex = extra.getAttribute('tabindex') || '0';
          extra.setAttribute('tabindex', '-1');
        }
      }
      index = next;
    }
    const distinct = cards.filter(card => !card.classList.contains('cps-grid-hidden-media'));
    distinct.slice(shown).forEach(card => card.classList.add('cps-grid-hidden-batch'));
    if (!nativeMore() && distinct.length <= shown && distinct.length % 4 === 2) {
      distinct.slice(-2).forEach(card => card.classList.add('cps-grid-fill-two'));
    }
    return {cardCount: cards.length, distinctCount: distinct.length};
  };

  const updateButton = distinctCount => {
    if (!moreButton) return;
    moreButton.hidden = distinctCount <= shown && !nativeMore();
    moreButton.disabled = loading;
    moreButton.textContent = loading ? 'Loading customer repairs…' : 'Show more customer repairs';
  };

  const finishLoading = distinctCount => {
    loading = false;
    pending = false;
    clearTimeout(requestTimer);
    updateButton(distinctCount);
  };

  const pump = ({cardCount, distinctCount}) => {
    if (!loading) return;
    if (distinctCount >= shown || !nativeMore() || requests >= 10) {
      finishLoading(distinctCount);
      return;
    }
    if (pending) {
      if (cardCount <= requestedCards) return;
      pending = false;
      clearTimeout(requestTimer);
    }
    const next = nativeMore();
    if (!next || next.disabled) return;
    requests += 1;
    pending = true;
    requestedCards = cardCount;
    next.click();
    requestTimer = setTimeout(() => {
      if (!pending) return;
      finishLoading(grid.querySelectorAll('.jdgm-review-card:not(.cps-grid-hidden-media)').length);
    }, 10000);
  };

  const connect = () => {
    grid = section.querySelector('.jdgm-grid-body');
    if (!grid) return false;
    moreButton = document.createElement('button');
    moreButton.className = 'cps-grid-more';
    moreButton.type = 'button';
    moreButton.setAttribute('aria-live', 'polite');
    moreButton.addEventListener('click', () => {
      if (loading) return;
      shown += batchSize;
      loading = true;
      requests = 0;
      update();
    });
    section.querySelector('.section-padding')?.append(moreButton);
    section.classList.add('cps-grid-controlled');
    let scheduled = false;
    const update = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const state = enhance(grid);
        updateButton(state.distinctCount);
        pump(state);
      });
    };
    new MutationObserver(update).observe(grid, { childList: true });
    loading = true;
    update();
    return true;
  };

  if (connect()) return;
  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    if (connect() || attempts >= 80) clearInterval(timer);
  }, 250);
})();
